import { Component } from '@angular/core';
import { ExportTemplate, ExportTemplateFilters } from 'src/app/components/company/models/new/export-template';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { TemplatesQueries } from 'src/app/shared/types/get-query';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';
import { ExportTemplateMapper } from '../../../services/export-template-mapper';
import { map } from 'rxjs';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { FilterListComponent } from 'src/app/shared/components/filter-list/filter-list.component';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';
import { Brand } from '../../../models/new/brand.model';
import { Category } from '../../../models/new/category';
import { StyleGroup } from '../../../models/new/style-group';
import { BrandFilterService } from 'src/app/services/filters/brand-filter.service';
import { CategoryFilterService } from 'src/app/services/filters/category-filter.service';
import { StyleGroupFilterService } from 'src/app/services/filters/style-group-filter.service';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Paginable } from 'src/app/shared/types/paginable';
import { Selectable } from 'src/app/shared/types/selectable.type';
import { ExportChannel } from '../../../models/new/export-channel';
import { ExportChannelMapper } from '../../../services/export-channel-mapper';

@Component({
	standalone: false,
	selector: 'bt-company-export-template-list',
	templateUrl: './company-export-template-list.component.html',
	styleUrls: ['./company-export-template-list.component.scss']
})
export class CompanyExportTemplateListComponent {
	exportTemplates: Paginable<Selectable<ExportTemplate>> = new Paginable<Selectable<ExportTemplate>>();
	exportTemplateFilters: ExportTemplateFilters = new ExportTemplateFilters();
	exportChannel: ExportChannel | undefined;
	loading = false;

	constructor(
		private graphQLService: GraphQLService,
		private utilitiesService: UtilitiesService,
		private exportTemplateMapper: ExportTemplateMapper,
		private exportChannelMapper: ExportChannelMapper,
		private dialog: MatDialog,
		private brandFilterService: BrandFilterService,
		private categoryFilterService: CategoryFilterService,
		private styleGroupFilterService: StyleGroupFilterService,
		private router: Router,
		private route: ActivatedRoute
	) {}

	ngOnInit() {
		this.route.params.subscribe((params) => {
			this.loadExportChannel(Number(params['channelId']));
		});
	}

	loadExportChannel(channelId?: number) {
		if (!channelId) {
			this.router.navigate(['/company/third-parties-applications']);
			return;
		}

		this.graphQLService
			.getData<any>(TemplatesQueries.GET_EXPORT_CHANNELS, {
				where: this.exportChannelMapper.toWhereClauseById(channelId)
			})
			.pipe(
				map((res: any) =>
					(res.data.exportChannels?.nodes || []).map((node: any) => this.exportChannelMapper.toExportChannel(node))
				)
			)
			.subscribe({
				next: (channels: ExportChannel[]) => {
					this.exportChannel = channels[0];

					if (!this.exportChannel) {
						this.router.navigate(['/company/third-parties-applications']);
						return;
					}

					this.getExportTemplates();
				},
				error: () => {
					this.router.navigate(['/company/third-parties-applications']);
				}
			});
	}

	onBack(): void {
		this.router.navigate(['/company/third-parties-applications']);
	}

	pageEvent(event: { pageNumber: number; pageSize: number }) {
		this.exportTemplates.currentPage = event.pageNumber;
		this.exportTemplates.size = event.pageSize;

		this.exportTemplateFilters.page = event.pageNumber;
		this.exportTemplateFilters.size = event.pageSize;

		this.refreshNavigation();
	}

	refreshNavigation() {
		this.loading = true;

		this.router
			.navigate([location.pathname], {
				queryParams: this.exportTemplateMapper.filtersToUrl(this.exportTemplateFilters)
			})
			.then((navigated) => {
				// Stessi filtri = stesso URL: il router ignora la navigazione e la lista non si ricarica
				if (!navigated) {
					this.loading = false;
				}
			});
	}

	prepareFilterOptions(params: Params) {
		//questi chiamate servono per popolare il nome delle tendine di parametri in input tramite url

		if (params['brands'] && params['brands'].length) {
			this.brandFilterService.searchById(params['brands']).subscribe((res: Brand[]) => {
				this.exportTemplateFilters.brands?.forEach((brand) => {
					const found = res.find((b) => b.id == brand.id);
					brand.name = found ? found.name : '';
				});
			});
		}

		if (params['categories'] && params['categories'].length) {
			this.categoryFilterService.searchById(params['categories']).subscribe((res: Category[]) => {
				this.exportTemplateFilters.categories?.forEach((category) => {
					const found = res.find((c) => c.id == category.id);
					category.name = found ? found.name : '';
				});
			});
		}

		if (params['styleGroups'] && params['styleGroups'].length) {
			this.styleGroupFilterService.searchById(params['styleGroups']).subscribe((res: StyleGroup[]) => {
				this.exportTemplateFilters.styleGroups?.forEach((styleGroup) => {
					const found = res.find((sg) => sg.id == styleGroup.id);
					styleGroup.name = found ? found.name : '';
				});
			});
		}
	}

	getExportTemplates() {
		this.loading = true;

		this.route.queryParams.subscribe((params: Params) => {
			this.exportTemplateFilters = this.exportTemplateMapper.urlToFilters(params);
			this.exportTemplateFilters.exportChannelId = this.exportChannel?.id;

			this.exportTemplates.currentPage = this.exportTemplateFilters.page || 1;
			this.exportTemplates.size = this.exportTemplateFilters.size || 2;

			//metodo che popola le label dei filtri in base agli id passati da url,
			this.prepareFilterOptions(params);

			const pagination = this.exportTemplates.buildQuery(this.exportTemplates.currentPage);
			this.graphQLService
				.getData<any>(TemplatesQueries.GET_EXPORT_TEMPLATES, {
					where: this.exportTemplateMapper.toWhereClause(this.exportTemplateFilters),
					...pagination
				})
				.pipe(
					map((res: any) => {
						const exportTemplates = res.data.exportCodifiedUseMaps;

						return new Paginable<Selectable<ExportTemplate>>({
							items: exportTemplates.nodes.map((node: any) => ({
								...this.exportTemplateMapper.toExportTemplate(node),
								selected: false
							})),
							totalCount: exportTemplates.totalCount,
							currentPage: this.exportTemplates.currentPage,
							size: this.exportTemplates.size
						});
					})
				)
				.subscribe({
					next: (data: Paginable<Selectable<ExportTemplate>>) => {
						this.exportTemplates = data;
						this.loading = false;
					},
					error: (err: any) => {
						console.error('Error loading export templates', err);
						this.utilitiesService.showSnackbar('Error loading export templates', SnackbarType.Error);
						this.loading = false;
					}
				});
		});
	}

	cleanFilter(filter: string) {
		switch (filter) {
			case 'brands':
				this.exportTemplateFilters.brands = [];
				break;
			case 'categories':
				this.exportTemplateFilters.categories = [];
				break;
			case 'styleGroups':
				this.exportTemplateFilters.styleGroups = [];
				break;
			default:
				break;
		}

		//se si pulisce un filtro, porta a pagina 1
		this.exportTemplateFilters.page = 1;

		this.refreshNavigation();
	}

	openBrandFilterModal(): void {
		this.dialog
			.open(FilterListComponent<Brand>, {
				data: {
					title: 'Brands',
					selected: this.exportTemplateFilters.brands
						? this.exportTemplateFilters.brands.map((el) => ({
								...el,
								displayId: el.id,
								displayName: el.name,
								selected: true
							}))
						: [],
					filterService: this.brandFilterService
				},
				width: '400px'
			})
			.afterClosed()
			.subscribe({
				next: (res: Array<Selectable<Brand>>) => {
					if (res) {
						this.exportTemplateFilters.brands = res.map((el: any) => new Brand({ id: el.displayId, name: el.displayName }));

						this.refreshNavigation();
					}
				}
			});
	}

	openCategoryFilterModal(): void {
		this.dialog
			.open(FilterListComponent, {
				data: {
					title: 'Categories',
					selected:
						this.exportTemplateFilters.categories?.map((el) => ({
							...el,
							displayId: el.id,
							displayName: el.name,
							selected: true
						})) || [],
					filterService: this.categoryFilterService
				},
				width: '400px'
			})
			.afterClosed()
			.subscribe({
				next: (res: Array<Selectable<Category>>) => {
					if (res) {
						this.exportTemplateFilters.categories = res.map((el: any) => ({ id: el.displayId, name: el.displayName }));
						this.refreshNavigation();
					}
				}
			});
	}

	openStyleGroupFilterModal(): void {
		this.dialog
			.open(FilterListComponent, {
				data: {
					title: 'Style Groups',
					selected:
						this.exportTemplateFilters.styleGroups?.map((el) => ({
							...el,
							displayId: el.id,
							displayName: el.name,
							selected: true
						})) || [],
					filterService: this.styleGroupFilterService
				},
				width: '400px'
			})
			.afterClosed()
			.subscribe({
				next: (res: Array<Selectable<StyleGroup>>) => {
					if (res) {
						this.exportTemplateFilters.styleGroups = res.map((el: any) => ({ id: el.displayId, name: el.displayName }));
						this.refreshNavigation();
					}
				}
			});
	}

	removeExportTemplate(exportTemplate: Selectable<ExportTemplate>) {
		this.dialog
			.open(ConfirmModalComponent, {
				data: {
					title: 'Remove Export Template',
					message: 'Are you sure you want to remove this export template?'
				}
			})
			.afterClosed()
			.subscribe((confirm: boolean) => {
				if (confirm) {
					this.graphQLService.postData(MutationQuery.DELETE_EXPORT_TEMPLATE, { input: this.exportTemplateMapper.toDeleteExportTemplateQL(exportTemplate.id!) }).subscribe({
						next: () => {
							this.utilitiesService.showSnackbar('Export template removed successfully', SnackbarType.Success);
							this.getExportTemplates();
						},
						error: () => {
							this.utilitiesService.showSnackbar('Error removing export template', SnackbarType.Error);
						}
					});
				}
			});
	}
}
